/**
 * ``useTracksFeed`` — TanStack Query hook around
 * ``GET /api/query/tracks``, seeding the shared
 * :func:`map/track-store` so the per-sensor dynamic-track tails appear
 * fully-formed on map load / after a pan (issue #278 follow-up, PR2).
 *
 * # Why a backfill feed
 *
 * PR1 fed the track store from the live status SSE stream only — tails
 * built up over the next few status updates after the map opened. This
 * hook adds the **one-shot server backfill**: on the first load (and on
 * each viewport change) it fetches the recent samples for every Producer
 * in the viewport and {@link TrackState.seed | seeds} them into the store,
 * so a tail is drawn immediately rather than accreting live. Between
 * viewport changes the live SSE bridge (:func:`map/use-track-sse-bridge`)
 * keeps the tail's leading edge sharp — so there is **no poll interval**
 * here (unlike :func:`detections/use-recent-detections-feed`); the backfill fires
 * once per distinct viewport, live SSE handles freshness in between.
 *
 * # Query key — stable, no Date, quantised viewport
 *
 * Keyed on ``viewport + window_seconds + limit_per_producer``. Unlike
 * :func:`useSnapshot` / :func:`useTrail` (which anchor a ``since`` /
 * ``start_time`` ``Date`` and must guard against re-anchoring every
 * render), the BFF's ``/tracks`` route computes ``since = now -
 * window_seconds`` **server-side** — so the client passes a constant
 * ``window_seconds`` and the key is composed entirely of stable numbers.
 * A pan/zoom re-keys (the viewport changes) and a fresh backfill fires
 * for the new bbox; a constant viewport never thrashes the key.
 *
 * The viewport enters the key **and** the URL through
 * :func:`quantizeViewport` (outward snap to the 1e-4° grid). Keyed on the
 * raw floats, every settle of the camera on the *same* view — the
 * first-load home ``flyTo``, an inertia settle — re-keyed this query on a
 * last-digit difference and refetched a ~70 KB response the SSE bridge had
 * already kept current (ADR-016 amendment 2026-10-01: ~2.5 requests per
 * distinct viewport). Quantised, those twins share one key. The server
 * contract is unchanged: the route still receives four bbox floats, merely
 * on the grid and at most one cell (≤ 11 m) wider per edge than the screen.
 *
 * # Refetch policy
 *
 * ``refetchOnWindowFocus`` is **off**: between backfills the live SSE
 * bridge keeps the tails current, so a focus-triggered refetch would only
 * repeat the window-function query for data the store already holds.
 * ``refetchOnMount`` keeps TanStack's default (refetch when the cached
 * entry is older than :const:`TRACKS_FEED_STALE_TIME_MS`): a remount is an
 * explicit layout action by the operator, rare, and the conservative
 * choice (CLAUDE.md §8.1) is to resync the picture once rather than trust
 * a cache of unknown age. ``refetchOnReconnect`` keeps its default too —
 * a browser ``online`` event is a genuine resync signal.
 *
 * # Seeding (merge, not replace)
 *
 * On each successful fetch the hook calls ``seed(producer_id, points)``
 * per Producer. ``seed`` **merges** the backfill with any points the live
 * SSE bridge already accumulated (union, dedupe by ``time``, sort
 * ascending, ring-cap) — so the older backfill history slots in front of
 * newer live points without double-plotting the overlap. Off-viewport
 * Producers keep their existing store entries (the store is a client-side
 * buffer, not viewport-scoped); the tail layer's km-trim + the ring cap
 * bound what's visible + retained.
 *
 * # Role gating
 *
 * Same map-reader bundle as :func:`useSnapshot` — the BFF gates
 * ``/api/query/tracks`` on ``Depends(require_roles(...map_reader...))``;
 * the UI mirrors with :const:`MAP_READER_ROLES` as defense-in-depth.
 *
 * Cross-references:
 *   - REQ-UI-012 (trail rendering precedent).
 *   - REQ-UI-026 (TanStack Query for server state).
 *   - REQ-UI-027 (Zustand for client state — the track store).
 *   - REQ-UI-035 (BFF-only).
 *   - REQ-AUTH-022 / 024..027 (RBAC mirroring).
 *   - ADR-016 §6 (dynamic track tails).
 *   - packages/meridian_bff/src/meridian_bff/_query_routes.py §tracks_route.
 *   - packages/meridian_bff/api/openapi.yaml §TracksResponse.
 */
import { useEffect } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useSession } from "@/auth/session-context";
import { apiFetch, ApiError } from "@/lib/api-client";
import { MAP_READER_ROLES } from "@/map/use-snapshot";
import type { SnapshotViewport } from "@/map/snapshot-types";
import { quantizeViewport } from "@/map/viewport-sync";
import { type TrackPoint, useTrackStore } from "@/map/track-store";
import type { components } from "@/lib/api-types.gen";

/** Wire response for ``GET /api/query/tracks`` (codegen). */
export type TracksResponse = components["schemas"]["TracksResponse"];
type ProducerTrackItem = components["schemas"]["ProducerTrackItem"];

/**
 * Backfill window length in seconds. Mirrors the BFF route default
 * (``window_seconds=900``) + the 15-minute trail window per ADR-016 §5 /
 * Open Item 3, so a freshly-loaded tail spans the same horizon as the
 * selected-Producer trail.
 */
export const TRACKS_FEED_WINDOW_SECONDS = 900;

/**
 * Per-Producer sample cap requested from the BFF. Mirrors the route
 * default (``limit_per_producer=200``). The km-trim at render keeps the
 * visible tail far shorter; this only bounds the backfill payload.
 */
export const TRACKS_FEED_LIMIT_PER_PRODUCER = 200;

/**
 * Default :class:`useQuery` stale time, in milliseconds. A pan within this
 * window re-uses the cached backfill for the same viewport key. Matches
 * :const:`SNAPSHOT_STALE_TIME_MS`.
 */
export const TRACKS_FEED_STALE_TIME_MS = 5_000;

/**
 * Compose the BFF URL for a viewport tracks backfill.
 *
 * The bbox is :func:`quantizeViewport`-snapped first, so two sub-grid
 * twins compose the **same** URL (module docstring §"Query key").
 * Exported for unit-test reach into the query-string composition without
 * spinning up the full hook + msw server. Mirrors
 * :func:`composeSnapshotUrl`.
 */
export function composeTracksUrl(
  viewport: SnapshotViewport,
  opts: { windowSeconds: number; limitPerProducer: number },
): string {
  const bbox = quantizeViewport(viewport);
  const params = new URLSearchParams({
    min_longitude: bbox.min_longitude.toString(),
    min_latitude: bbox.min_latitude.toString(),
    max_longitude: bbox.max_longitude.toString(),
    max_latitude: bbox.max_latitude.toString(),
    window_seconds: opts.windowSeconds.toString(),
    limit_per_producer: opts.limitPerProducer.toString(),
  });
  return `/api/query/tracks?${params.toString()}`;
}

/**
 * The query-key shape — exported so consumers
 * (:func:`useQueryClient().invalidateQueries`) can target it. Carries the
 * :func:`quantizeViewport`-snapped bounds, never the raw floats.
 */
export function tracksQueryKey(
  viewport: SnapshotViewport,
  windowSeconds: number,
  limitPerProducer: number,
): readonly unknown[] {
  const bbox = quantizeViewport(viewport);
  return [
    "tracks",
    bbox.min_longitude,
    bbox.min_latitude,
    bbox.max_longitude,
    bbox.max_latitude,
    windowSeconds,
    limitPerProducer,
  ];
}

/** Options for :func:`useTracksFeed`. */
export interface UseTracksFeedOptions {
  /** Bounding box to backfill. ``null`` disables the query — gates on map
   *  readiness (bounds aren't known until the map's first ``load``). */
  viewport: SnapshotViewport | null;
  /** Override the backfill window (seconds). Production omits; tests use
   *  it to assert composition. */
  windowSeconds?: number;
  /** Override the per-Producer sample cap. Production omits. */
  limitPerProducer?: number;
  /** Override the stale time. Tests set 0; production omits. */
  staleTimeMs?: number;
}

/** Map a wire :type:`ProducerTrackItem` point list to store points. */
function pointsFromApi(item: ProducerTrackItem): readonly TrackPoint[] {
  return item.points.map((p) => ({
    latitude_deg: p.latitude_deg,
    longitude_deg: p.longitude_deg,
    time: p.time,
    health_state: p.health_state,
  }));
}

/**
 * Backfill the per-sensor track tails for the current viewport and seed
 * the shared track store.
 *
 * Safe to mount alongside the live SSE bridge: ``seed`` merges the
 * backfill with already-accumulated live points.
 *
 * @param options - Viewport + optional window / cap / stale-time overrides.
 * @returns TanStack Query result (so the caller can observe loading /
 *     error state if it wants to; the Map mounts it for the side-effect).
 */
export function useTracksFeed(
  options: UseTracksFeedOptions,
): UseQueryResult<TracksResponse, ApiError> {
  const session = useSession();
  const hasReadRole = session.hasAnyRole(MAP_READER_ROLES);
  const windowSeconds = options.windowSeconds ?? TRACKS_FEED_WINDOW_SECONDS;
  const limitPerProducer = options.limitPerProducer ?? TRACKS_FEED_LIMIT_PER_PRODUCER;
  const { viewport } = options;
  const enabled = viewport !== null && hasReadRole;

  const query = useQuery<TracksResponse, ApiError>({
    queryKey:
      viewport !== null
        ? tracksQueryKey(viewport, windowSeconds, limitPerProducer)
        : ["tracks", "disabled"],
    queryFn: async ({ signal }) => {
      if (viewport === null) {
        // ``enabled: false`` should prevent this branch, but the type
        // system requires queryFn to be unconditional. Surface loudly per
        // CLAUDE.md §8.1 (operator-visibility).
        throw new ApiError(0, null, "useTracksFeed: query ran without viewport");
      }
      const url = composeTracksUrl(viewport, { windowSeconds, limitPerProducer });
      const raw = await apiFetch<unknown>(url, { signal });
      if (!isTracksResponse(raw)) {
        // Boundary-validation failure — the BFF surface shifted. Halt with
        // a structured error per CLAUDE.md §8.1 (silent failures forbidden).
        console.error(
          "[meridian/map/use-tracks-feed] tracks response failed shape validation",
          raw,
        );
        throw new ApiError(500, raw, "tracks response did not match the expected shape");
      }
      return raw;
    },
    enabled,
    staleTime: options.staleTimeMs ?? TRACKS_FEED_STALE_TIME_MS,
    // The live SSE bridge is the freshness mechanism between backfills; a
    // window-focus refetch would repeat the heavy query for nothing. Mount
    // and reconnect keep TanStack's defaults — module docstring §"Refetch
    // policy".
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => {
      if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // Seed the shared track store on each successful fetch. Keyed on the
  // data reference so it runs once per distinct response (TanStack returns
  // a stable reference for an unchanged cache entry). ``seed`` merges the
  // backfill with any live-accumulated points per Producer.
  const data = query.data;
  useEffect(() => {
    if (data !== undefined) {
      const store = useTrackStore.getState();
      for (const track of data.tracks) {
        store.seed(track.producer_id, pointsFromApi(track));
      }
    }
  }, [data]);

  return query;
}

/** Narrow an ``unknown`` BFF payload to :type:`TracksResponse`. */
function isTracksResponse(value: unknown): value is TracksResponse {
  if (typeof value !== "object" || value === null) return false;
  const obj = value as Record<string, unknown>;
  return Array.isArray(obj.tracks) && typeof obj.duration_ms === "number";
}
